import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, Outlet, useNavigate } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import { useLayoutContext, useUser } from "@app/AppProvider.tsx";
import { Breadcrumbs } from "@components/breadcrumbs/Breadcrumbs.tsx";
import { PageTitle } from "@components/PageTitle.tsx";
import { SupportPreviewToggle } from "@components/SupportPreviewToggle.tsx";
import { getMenuItems } from "@utils/menuItems.ts";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout
});

function RootLayout() {
  const { t, i18n } = useTranslation();
  const { language, addMenuItems, setMFNavigate } = useLayoutContext();
  const navigate = useNavigate();
  const { permissions } = useUser();

  // biome-ignore lint/correctness/useExhaustiveDependencies: No need to include all dependencies in the dependency array
  useEffect(() => {
    i18n.changeLanguage(language);
    addMenuItems(getMenuItems(t, (path: string) => navigate({ to: path }), permissions));
    setMFNavigate((path: string) => navigate({ to: path }));
  }, [language, permissions]);

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <Breadcrumbs />
        <SupportPreviewToggle />
      </div>
      <PageTitle />
      <Outlet />
      <TanStackDevtools
        plugins={[
          {
            name: "Query",
            render: <ReactQueryDevtoolsPanel />,
            defaultOpen: true
          },
          {
            name: "Router",
            render: <TanStackRouterDevtoolsPanel />
          }
        ]}
      />
    </>
  );
}
